import { gql, useMutation } from "@apollo/client";
import { useCallback, useMemo } from "react";

const UPDATE_AI_INTERVIEWER_AGENT_SETTINGS = gql`
  mutation UpdateAiInterviewerAgentSettings(
    $aiInterviewerId: ID!
    $ttsSettings: JSONString
  ) {
    updateAiInterviewerAgentSettings(
      aiInterviewerId: $aiInterviewerId
      ttsSettings: $ttsSettings
    ) {
      aiInterviewer {
        id
        aiInterviewerAgentSettings {
          ttsSettings
        }
      }
    }
  }
`;

interface UseSpeechSpeedUpdateProps {
  aiInterviewerId: string;
  ttsSettingsJsonString: string | undefined;
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}

export const useSpeechSpeedUpdate = ({
  aiInterviewerId,
  ttsSettingsJsonString,
  onSuccess,
  onError,
}: UseSpeechSpeedUpdateProps): {
  updateSpeechSpeed: (speed: number) => Promise<void>;
  loading: boolean;
  error: any;
} => {
  const ttsSettings = useMemo(
    () => JSON.parse(ttsSettingsJsonString ?? "{}") as Record<string, unknown>,
    [ttsSettingsJsonString]
  );

  const [updateAgentSettings, { loading, error }] = useMutation(
    UPDATE_AI_INTERVIEWER_AGENT_SETTINGS
  );

  const updateSpeechSpeed = useCallback(
    async (speed: number): Promise<void> => {
      try {
        const updatedTtsSettings = {
          ...ttsSettings,
          provider: "elevenlabs",
          // eslint-disable-next-line camelcase
          voice_settings: {
            ...(ttsSettings.voice_settings ?? {}),
            speed,
          },
        };

        await updateAgentSettings({
          variables: {
            aiInterviewerId,
            ttsSettings: JSON.stringify(updatedTtsSettings),
          },
        });

        onSuccess?.();
      } catch (err) {
        onError?.(err as Error);
      }
    },
    [aiInterviewerId, ttsSettings, updateAgentSettings, onSuccess, onError]
  );

  return {
    updateSpeechSpeed,
    loading,
    error,
  };
};
